/**
 * Pure calculation helpers for bill line items and totals.
 * Similar to invoiceCalculations.js but without discount support.
 */

/**
 * Calculate the total price for a single line item (no discount).
 */
export function calculateLineTotal(qty, unitPrice, taxRate, taxInclusion) {
  // The Amount column reflects the line amount in the same tax basis as the entered
  // unit price (Xero-style): ex-tax when "Tax exclusive", inc-tax when "Tax inclusive".
  // Tax is shown separately in the Tax amount column and added to the total.
  return qty * unitPrice;
}

/**
 * Calculate the tax amount for a single line item.
 */
export function calculateLineTax(lineItem, taxes, taxInclusion) {
  if (taxInclusion === 'no_tax' || !lineItem.tax_rate_id) return 0;

  const selectedTax = taxes.find((t) => t.tax_id === lineItem.tax_rate_id);
  const taxRate = selectedTax ? parseFloat(selectedTax.tax_rate) / 100 : 0;

  const qty = lineItem.quantity || 0;
  const price = lineItem.unit_price || 0;

  if (taxInclusion === 'tax_inclusive') {
    const totalPrice = lineItem.total_price || 0;
    const subtotal = totalPrice / (1 + taxRate);
    return totalPrice - subtotal;
  }
  if (taxInclusion === 'tax_exclusive') {
    return qty * price * taxRate;
  }
  return 0;
}

/**
 * Calculate bill-level totals from all line items.
 */
export function calculateBillTotals(lineItems, taxes, taxInclusion) {
  let subtotal = 0;
  let taxTotal = 0;

  lineItems.forEach((item) => {
    const qty = item.quantity || 0;
    const price = item.unit_price || 0;
    const taxRateId = item.tax_rate_id || 'GST';
    const selectedTax = taxes.find((t) => t.tax_id === taxRateId);
    const taxRate = selectedTax ? parseFloat(selectedTax.tax_rate) / 100 : 0;

    let itemSubtotal;
    let itemTax;

    if (taxInclusion === 'tax_inclusive') {
      const itemTotal = item.total_price || 0;
      itemSubtotal = itemTotal / (1 + taxRate);
      itemTax = itemTotal - itemSubtotal;
    } else if (taxInclusion === 'tax_exclusive') {
      itemSubtotal = qty * price;
      itemTax = itemSubtotal * taxRate;
    } else {
      itemSubtotal = qty * price;
      itemTax = 0;
    }

    subtotal += itemSubtotal;
    taxTotal += itemTax;
  });

  return { subtotal, taxTotal, grandTotal: subtotal + taxTotal };
}

/**
 * Get the tax rate (as a decimal, e.g. 0.10) for a given tax_rate_id.
 */
export function getTaxRate(taxRateId, taxes) {
  const selected = taxes.find((t) => t.tax_id === taxRateId);
  return selected ? parseFloat(selected.tax_rate) / 100 : 0.1;
}
